import request from './request';

/**
 * Required prefix for S3 keys (server enforces this for presign operations)
 */
const STORAGE_PREFIX = 'user_uploads/';

/**
 * Ensure key has the required storage prefix
 * @param {string} key - The S3 object key
 * @returns {string} Key with prefix
 */
const ensurePrefix = (key) => {
  if (!key) return STORAGE_PREFIX;
  return key.startsWith(STORAGE_PREFIX) ? key : `${STORAGE_PREFIX}${key}`;
};

/**
 * Allowed content types for upload (matching server whitelist)
 */
export const ALLOWED_CONTENT_TYPES = new Set([
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
  'image/webp',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/msword',
  'application/vnd.ms-excel',
  'text/plain',
  'text/csv',
  'application/json',
  'application/octet-stream',
]);

/**
 * List files and folders at a given prefix
 * @param {string} [prefix] - Optional prefix/folder path to list
 * @param {string} [continuationToken] - Token for pagination
 * @returns {Promise<{prefix: string, folders: Array, files: Array, is_truncated: boolean, next_continuation_token?: string}>}
 */
export const listFiles = (prefix, continuationToken) => {
  const params = new URLSearchParams();
  // Add prefix for subfolder listings (server handles user scoping)
  if (prefix) {
    // Ensure prefix ends with trailing slash for folder listing
    const normalizedPrefix = prefix.endsWith('/') ? prefix : `${prefix}/`;
    params.append('prefix', normalizedPrefix);
  }
  if (continuationToken) params.append('continuation_token', continuationToken);
  const queryString = params.toString();
  return request.get(`/api/v1/storage/list/${queryString ? `?${queryString}` : ''}`);
};

/**
 * Get a presigned URL for an S3 operation
 * @param {'upload' | 'download' | 'delete'} action - The operation type
 * @param {string} key - The S3 object key (full path)
 * @param {string} [contentType] - Content type (required for upload)
 * @param {number} [expiresIn] - URL expiration in seconds (max 3600)
 * @returns {Promise<{url: string}>}
 */
export const getPresignedUrl = (action, key, contentType, expiresIn) => {
  const payload = { key: ensurePrefix(key) };
  if (contentType) payload.content_type = contentType;
  if (expiresIn) payload.expires_in = expiresIn;
  return request.post(`/api/v1/storage/presign/${action}/`, payload);
};

/**
 * Upload a file to S3 using presigned URL
 * @param {File} file - The file to upload
 * @param {string} key - The S3 object key (full path including filename)
 * @param {function} [onProgress] - Progress callback (0-100)
 * @returns {Promise<void>}
 */
export const uploadFile = async (file, key, onProgress) => {
  // Validate content type
  const contentType = file.type || 'application/octet-stream';
  if (!ALLOWED_CONTENT_TYPES.has(contentType)) {
    throw new Error(`Content type not allowed: ${contentType}`);
  }

  // Get presigned URL
  const { data } = await getPresignedUrl('upload', key, contentType);
  const presignedUrl = data.url;

  // Upload directly to S3 with progress tracking
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const percent = Math.round((event.loaded / event.total) * 100);
        onProgress(percent);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed: ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed: Network error'));
    });

    xhr.open('PUT', presignedUrl);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.send(file);
  });
};

/**
 * Download a file from S3 using presigned URL
 * @param {string} key - The S3 object key (full path)
 * @param {string} [filename] - Optional filename for the download
 * @returns {Promise<void>}
 */
export const downloadFile = async (key, filename) => {
  // Get presigned URL
  const { data } = await getPresignedUrl('download', key);
  const presignedUrl = data.url;

  // Fetch the file
  const response = await fetch(presignedUrl);
  if (!response.ok) {
    throw new Error(`Download failed: ${response.status}`);
  }

  // Create blob and trigger download
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || key.split('/').pop() || 'download';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Delete a file from S3 using presigned URL
 * @param {string} key - The S3 object key (full path)
 * @returns {Promise<void>}
 */
export const deleteFile = async (key) => {
  // Get presigned URL
  const { data } = await getPresignedUrl('delete', key);
  const presignedUrl = data.url;

  // Delete from S3
  const response = await fetch(presignedUrl, { method: 'DELETE' });
  if (!response.ok) {
    throw new Error(`Delete failed: ${response.status}`);
  }
};

/**
 * Create a folder (empty object with trailing slash)
 * @param {string} folderPath - The folder path (will append trailing slash if missing)
 * @returns {Promise<void>}
 */
export const createFolder = async (folderPath) => {
  // Ensure trailing slash for folder
  const key = folderPath.endsWith('/') ? folderPath : `${folderPath}/`;
  
  // Get presigned URL for upload
  const { data } = await getPresignedUrl('upload', key, 'application/octet-stream');
  const presignedUrl = data.url;

  // Upload empty content to create folder marker
  const response = await fetch(presignedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/octet-stream' },
    body: '',
  });

  if (!response.ok) {
    throw new Error(`Failed to create folder: ${response.status}`);
  }
};
